import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, Input } from '@angular/core';
import { ProductAddToBasketComponentDef } from '@df/catalog-ui/product/add-to-basket/product-add-to-basket.component.def';
import { IProductDataV2 } from '@df/catalog/product/product';
import { Client } from '@df/core/service/client';
import { DfEventData, DfEventService } from '@df/core/service/df-event';
import { IfBrowserDirective } from '@df/ui/common/if-browser.directive';
import { TranslatePipe } from '@df/ui/translation/translate.pipe';
import { ProductDecorator } from '../../../../../../catalog/model/product/product.decorator';
import { ActionComponent } from '../../../../../../ui/api/action.component';
import { ResultComponent } from '../../../../../../ui/api/result.component';
import {
    PRODUCT_CLOSE_PREVIEW_EVENT_NAME,
    PRODUCT_OPEN_PREVIEW_EVENT_NAME,
    PRODUCT_PREPARE_PREVIEW_EVENT_NAME
} from '../../../product.abstract';
import { ProductService } from '../../../product.service';

@Component({
    selector: 'product-listing-add-to-basket-button',
    templateUrl: './product-listing-add-to-basket-button.component.html',
    styleUrl: './product-listing-add-to-basket-button.component.scss',
    standalone: true,
    imports: [ActionComponent, CommonModule, IfBrowserDirective, ResultComponent, TranslatePipe],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ProductListingAddToBasketButtonComponent extends ProductAddToBasketComponentDef {
    @Input({ required: true })
    override product!: ProductDecorator<IProductDataV2>;

    @Input()
    listPosition?: number;

    @Input()
    canAdd = false;

    protected productService = inject(ProductService);
    protected dfEventService = inject(DfEventService);
    readonly client = inject(Client);

    canPreview = true;

    /**
     * If quick add can be used
     */
    get disabled() {
        if (!this.canAdd) {
            return true;
        }

        if (this.product.parent) {
            return this.product.parent.isOutOfStock;
        }

        return !this.isSalable;
    }

    override ngOnInit(): void {
        this.subscriptions.add(
            this.dfEventService.success(event => {
                if (this.productService.canTriggerEvent(event, this.product, this.listPosition)) {
                    if (event.id === PRODUCT_PREPARE_PREVIEW_EVENT_NAME) {
                        this.onPrepareEvent(event);
                    }

                    if (event.id === PRODUCT_CLOSE_PREVIEW_EVENT_NAME) {
                        this.onCloseEvent(event);
                    }

                    if (event.id === PRODUCT_OPEN_PREVIEW_EVENT_NAME && (this.client.isS || this.client.isM)) {
                        this.status.busy = false;
                    }
                }
            })
        );

        super.ngOnInit();
    }

    /**
     * Initiate adding (openPreview) or add to basked
     */
    add() {
        if (this.disabled) {
            return;
        }

        this.dfEventService.dispatch({
            id: PRODUCT_PREPARE_PREVIEW_EVENT_NAME,
            data: {
                product: this.product,
                index: this.listPosition,
                callback: component => {
                    this.canPreview = component.canPreview;

                    if (!this.canPreview) {
                        this.submit();
                    }
                }
            }
        });
    }

    protected override onSuccess(result?: any): any {
        this.canPreview = true;
        return super.onSuccess(result);
    }

    protected override onError(result?: any): any {
        this.canPreview = true;
        return super.onError(result);
    }

    protected onPrepareEvent(event: DfEventData) {
        if (this.canPreview) {
            this.status.busy = true;
        }
    }

    protected onCloseEvent(event: DfEventData) {
        if (this.canPreview) {
            this.status.busy = false;
        }
    }
}
